import { createLucideIcon } from "lucide-react";

/*
 * Re-export custom icon SVGs  in Lucide Icon component.
 * https://lucide.dev/guide/packages/lucide-react#with-lucide-lab-or-custom-icons
 *
 * Steps to add a new custom icon:
 * 1. Export as SVG from Figma. Make sure to take it from the component library, not from a frame.
 * 2. Get the icon data. You can use `npm run convert-svg-to-lucide ~/path/to/icon.svg`
 * 3. Add the icon here, passing the output from ^ to `createLucideIcon`.
 * 4. Set `fill` to `currentColor`.
 * 5. Remove stroke width - if you set it here it will be hardcoded. If you don't set it it'll behave like Lucide's built in icons.
 * 6. Add a "key" to the path, the value doesn't matter. This acts as a React key, without it React will log an `Each child in a list should have a unique "key" prop` warning.
 *
 * Built in Lucide icons should still be imported as normal, you don't have to re-export them from here.
 */

export const Archive = createLucideIcon("Archive", [
  [
    "path",
    {
      d: "M3 7.5V17.4C3 18.6601 3 19.2897 3.24524 19.771C3.46095 20.1944 3.80491 20.5393 4.22827 20.755C4.7091 21 5.33887 21 6.59652 21H17.4035C18.6611 21 19.29 21 19.7708 20.755C20.1942 20.5393 20.5393 20.1944 20.755 19.771C21 19.2902 21 18.6611 21 17.4035V7.5M3 7.5H21M3 7.5L4.53582 4.81225C4.91273 4.15267 5.10114 3.82295 5.36616 3.58282C5.60082 3.3702 5.87841 3.20931 6.17944 3.11105C6.51968 3 6.89951 3 7.6604 3H16.3392C17.1001 3 17.4813 3 17.8216 3.11105C18.1226 3.20931 18.3989 3.3702 18.6335 3.58282C18.8988 3.82314 19.0877 4.15321 19.4652 4.81384L21 7.5M12 10.875V17.625M12 17.625L15.375 15.375M12 17.625L8.625 15.375",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const Archive2 = createLucideIcon("Archive2", [
  [
    "path",
    {
      d: "M3 7.5V17.4C3 18.66 3 19.29 3.24986 19.77C3.46974 20.19 3.80955 20.54 4.22932 20.75C4.70905 21 5.3387 21 6.598 21H17.402C18.6613 21 19.2909 21 19.7707 20.75C20.1904 20.53 20.5403 20.19 20.7501 19.77C21 19.29 21 18.66 21 17.4V7.5M3 7.5H20.99M3 7.5L4.53914 4.81C4.91893 4.15 5.10883 3.82 5.36868 3.58C5.59856 3.37 5.8784 3.21 6.17823 3.11C6.51805 3 6.89783 3 7.65741 3H16.3326C17.0922 3 17.472 3 17.8118 3.11C18.1116 3.21 18.3914 3.37 18.6213 3.58C18.8912 3.82 19.0711 4.15 19.4509 4.81L20.98 7.5M15.3731 13.25H8.62687",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const Chat = createLucideIcon("Chat", [
  [
    "path",
    {
      d: "M7.5 8.25H16.5M7.5 11.25H12M2.25 12.76C2.25 14.36 3.373 15.754 4.957 15.987C6.086 16.153 7.227 16.28 8.38 16.366C8.73 16.392 9.05 16.576 9.245 16.867L12 21L14.755 16.867C14.8516 16.7233 14.9798 16.6034 15.1297 16.5166C15.2795 16.4298 15.4472 16.3783 15.62 16.366C16.7652 16.2805 17.9069 16.1541 19.043 15.987C20.627 15.754 21.75 14.361 21.75 12.759V6.741C21.75 5.139 20.627 3.746 19.043 3.513C16.711 3.17072 14.357 2.99926 12 3C9.608 3 7.256 3.175 4.957 3.513C3.373 3.746 2.25 5.14 2.25 6.741V12.759V12.76Z",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const Density = createLucideIcon("Density", [
  [
    "path",
    {
      d: "M2 6.85556H11.1444M2 12H11.1444M2 17.1444H11.1444M19.1444 4V19.9889M19.1444 4L16.2889 6.85556M19.1444 4L22 6.85556M16.2889 17.1444L19.1444 20L22 17.1444",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const Forward = createLucideIcon("CustomForward", [
  [
    "path",
    {
      d: "M15.75 4.73999L22 10.99M22 10.99L15.75 17.24M22 10.99H4C2.9 10.99 2 11.89 2 12.99V19.26",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

// Native's `message-multiple-02.svg` (two chat bubbles), rescaled 32→24 grid so
// it renders on Lucide's default viewBox/stroke like its neighbors.
export const MessageMultiple = createLucideIcon("MessageMultiple", [
  [
    "path",
    {
      d: "M7.791 19C7.465 18.868 7.284 18.804 7.184 18.817C7.06 18.832 6.88 18.964 6.52 19.228C5.886 19.693 5.088 20.027 3.903 19.998C3.304 19.984 3.005 19.976 2.871 19.749C2.737 19.522 2.904 19.207 3.238 18.577C3.701 17.703 3.994 16.703 3.55 15.902C2.784 14.758 2.133 13.403 2.038 11.939C1.987 11.152 1.987 10.338 2.038 9.551C2.299 5.532 5.471 2.331 9.453 2.067C10.809 1.978 12.227 1.977 13.585 2.067C17.55 2.33 20.712 5.505 20.996 9.5M10.024 17.074C10.189 19.53 12.188 21.486 14.698 21.647C15.554 21.702 16.448 21.702 17.302 21.647C17.597 21.628 17.917 21.56 18.194 21.451C18.501 21.329 18.655 21.268 18.733 21.277C18.811 21.286 18.924 21.367 19.151 21.528C19.55 21.812 20.054 22.016 20.8 21.999C21.178 21.99 21.367 21.986 21.451 21.847C21.536 21.708 21.43 21.515 21.22 21.13C20.928 20.597 20.743 19.985 21.023 19.496C21.506 18.796 21.916 17.968 21.976 17.074C22.008 16.593 22.008 16.095 21.976 15.615C21.811 13.159 19.812 11.202 17.302 11.041C16.448 10.986 15.554 10.986 14.698 11.041C12.188 11.202 10.189 13.159 10.024 15.615C9.992 16.095 9.992 16.593 10.024 17.074Z",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const MoonBold = createLucideIcon("MoonBold", [
  [
    "path",
    {
      d: "M10.2961 2.07658C10.738 2.02013 10.9811 2.55926 10.7275 2.92547C9.95472 4.04131 9.53248 5.37389 9.53248 6.75C9.53248 8.53159 10.2402 10.2402 11.5 11.5C12.7598 12.7598 14.4684 13.4675 16.25 13.4675C17.6261 13.4675 18.9587 13.0453 20.0745 12.2725C20.4407 12.0189 20.9799 12.262 20.9234 12.7039C20.7379 14.1561 20.2184 15.5516 19.399 16.7779C18.3551 18.3402 16.8714 19.5578 15.1355 20.2769C13.3996 20.9959 11.4895 21.184 9.64664 20.8175C7.80382 20.4509 6.11108 19.5461 4.78248 18.2175C3.45388 16.8889 2.5491 15.1962 2.18254 13.3534C1.81598 11.5105 2.00411 9.6004 2.72314 7.86451C3.44217 6.12861 4.65981 4.64491 6.22208 3.60104C7.44842 2.78162 8.84388 2.26211 10.2961 2.07658Z",
      fill: "currentColor",
      key: "mainPath",
    },
  ],
]);

export const Reply = createLucideIcon("CustomReply", [
  [
    "path",
    {
      d: "M8.25 17.24L2 10.99M2 10.99L8.25 4.73999M2 10.99H20C21.1 10.99 22 11.89 22 12.99V19.26",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const Sparkle = createLucideIcon("Sparkle", [
  [
    "path",
    {
      d: "M9.563 15.654L8.75 18.5L7.937 15.654C7.72687 14.9189 7.3329 14.2494 6.79226 13.7087C6.25162 13.1681 5.58214 12.7741 4.847 12.564L2 11.75L4.846 10.937C5.58114 10.7269 6.25062 10.3329 6.79126 9.79226C7.3319 9.25162 7.72587 8.58214 7.936 7.847L8.75 5L9.563 7.846C9.77313 8.58114 10.1671 9.25062 10.7077 9.79126C11.2484 10.3319 11.9179 10.7259 12.653 10.936L15.5 11.75L12.654 12.563C11.9189 12.7731 11.2494 13.1671 10.7087 13.7077C10.1681 14.2484 9.77413 14.9179 9.564 15.653L9.563 15.654ZM18.009 8.465L17.75 9.5L17.491 8.465C17.3427 7.87159 17.036 7.32962 16.6036 6.89703C16.1712 6.46444 15.6294 6.15749 15.036 6.009L14 5.75L15.036 5.491C15.6294 5.34251 16.1712 5.03556 16.6036 4.60297C17.036 4.17038 17.3427 3.62841 17.491 3.035L17.75 2L18.009 3.035C18.1573 3.62854 18.4642 4.17059 18.8968 4.60319C19.3294 5.03579 19.8715 5.34267 20.465 5.491L21.5 5.75L20.465 6.009C19.8715 6.15733 19.3294 6.46421 18.8968 6.89681C18.4642 7.32941 18.1573 7.87147 18.009 8.465ZM16.644 20.317L16.25 21.5L15.856 20.317C15.7455 19.9856 15.5594 19.6845 15.3125 19.4375C15.0655 19.1906 14.7644 19.0045 14.433 18.894L13.25 18.5L14.433 18.106C14.7644 17.9955 15.0655 17.8094 15.3125 17.5625C15.5594 17.3155 15.7455 17.0144 15.856 16.683L16.25 15.5L16.644 16.683C16.7545 17.0144 16.9406 17.3155 17.1875 17.5625C17.4345 17.8094 17.7356 17.9955 18.067 18.106L19.25 18.5L18.067 18.894C17.7356 19.0045 17.4345 19.1906 17.1875 19.4375C16.9406 19.6845 16.7545 19.9856 16.644 20.317Z",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const Unarchive = createLucideIcon("Unarchive", [
  [
    "path",
    {
      d: "M2.995 7.5V17.4C2.995 18.66 2.995 19.29 3.245 19.77C3.465 20.19 3.805 20.54 4.225 20.75C4.705 21 5.335 21 6.595 21H17.405C18.665 21 19.295 21 19.775 20.75C20.195 20.53 20.545 20.19 20.755 19.77C21.005 19.29 21.005 18.66 21.005 17.4V7.5M2.995 7.5H20.995M2.995 7.5L4.535 4.81C4.915 4.15 5.105 3.82 5.365 3.58C5.595 3.37 5.87499 3.21 6.17499 3.11C6.51499 3 6.89499 3 7.65499 3H16.335C17.095 3 17.475 3 17.815 3.11C18.115 3.21 18.395 3.37 18.625 3.58C18.895 3.82 19.075 4.15 19.455 4.81L20.985 7.5M11.995 17.62V10.87M8.615 13.13L11.995 10.88L15.375 13.13",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      key: "mainPath",
    },
  ],
]);

export const Stop = createLucideIcon("Stop", [
  [
    "path",
    {
      d: "M6.9 6H17.7C17.9387 6 18.1676 6.09482 18.3364 6.2636C18.5052 6.43239 18.6 6.66131 18.6 6.9V17.7C18.6 17.9387 18.5052 18.1676 18.3364 18.3364C18.1676 18.5052 17.9387 18.6 17.7 18.6H6.9C6.66131 18.6 6.43239 18.5052 6.2636 18.3364C6.09482 18.1676 6 17.9387 6 17.7V6.9C6 6.66131 6.09482 6.43239 6.2636 6.2636C6.43239 6.09482 6.66131 6 6.9 6V6Z",
      fill: "currentColor",
      key: "mainPath",
    },
  ],
]);
